import { search } from "fast-fuzzy";
import { sortBy, uniqBy } from "lodash-es";
import { CategoryFragment } from "./useCategories.generated.js";

export type CategoryWithMatch = CategoryFragment & {
  match: { index: number; length: number }[] | undefined;
};

export type CategoryWithMatchAndChildren = CategoryWithMatch & {
  children?: CategoryWithMatchAndChildren[];
};

export const buildCategoryTree = (categories: CategoryWithMatch[]) => {
  const categoriesMap = new Map<string, CategoryWithMatchAndChildren>();
  const rootCategories: CategoryWithMatchAndChildren[] = [];

  categories.forEach(category => {
    categoriesMap.set(category.id, { ...category, children: [] });
  });

  categories.forEach(category => {
    const categoryWithChildren = categoriesMap.get(category.id)!;
    if (category.parentId) {
      const parent = categoriesMap.get(category.parentId);
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(categoryWithChildren);
      } else {
        rootCategories.push(categoryWithChildren);
      }
    } else {
      rootCategories.push(categoryWithChildren);
    }
  });

  return { categoriesTree: rootCategories, categoriesMap };
};

export const filterCategories = (
  categories: CategoryFragment[],
  searchTerm: string
): CategoryWithMatch[] => {
  if (!searchTerm.trim()) {
    return categories.map(c => ({
      ...c,
      match: undefined,
    })) as CategoryWithMatch[];
  }

  const categoriesParents = new Map<string, CategoryFragment[]>();
  const categoriesMap = new Map<string, CategoryFragment>();

  categories.forEach(category => {
    categoriesMap.set(category.id, category);
  });

  categories.forEach(category => {
    const parents: CategoryFragment[] = [];
    let current = category;
    while (current.parentId) {
      const parent = categoriesMap.get(current.parentId);
      if (!parent) break;
      parents.push(parent);
      current = parent;
    }
    categoriesParents.set(category.id, parents);
  });

  const searchResultCategories = search(searchTerm, categories, {
    keySelector: category => category.name,
    threshold: 0.7,
    returnMatchData: true,
  });

  return sortBy(
    uniqBy(
      [
        ...searchResultCategories.map(r => ({
          ...r.item,
          match: r.match ? [r.match] : undefined,
        })),
        ...searchResultCategories.flatMap(r =>
          (categoriesParents.get(r.item.id) ?? []).map(c => ({
            ...c,
            match: undefined,
          }))
        ),
      ],
      c => c.id
    ),
    c => c.rank
  ) as CategoryWithMatch[];
};

/**
 * Filters out categories whose parent (or any ancestor) already exists in the array.
 * This prevents showing redundant child categories when their parent is already included.
 *
 * Rules:
 * - Always keep 1st level categories (no parent)
 * - Always keep 2nd level categories (parent is root)
 * - Only filter out 3rd level and deeper when their ancestors are present
 */
export function removeChildrenOfIncludedCategories<
  T extends {
    id: string;
    parentId?: string | null;
    level: number;
    rank: number;
  },
>(categories: T[]): T[] {
  const categoryIds = new Set(categories.map(c => c.id));
  const categoriesMap = new Map(categories.map(c => [c.id, c]));

  // Helper to check if any ancestor is in the set
  const hasAncestorInSet = (category: T): boolean => {
    if (!category.parentId) return false;
    if (categoryIds.has(category.parentId)) return true;
    const parent = categoriesMap.get(category.parentId);
    return parent ? hasAncestorInSet(parent) : false;
  };

  const filteredCategories = categories.filter(category => {
    // Always keep level 1 (root) and level 2 categories
    if (category.level <= 2) return true;
    // For level 3 and deeper, filter out if ancestor is present
    return !hasAncestorInSet(category);
  });

  // Sort by level first, then by rank
  return sortBy(filteredCategories, [c => c.level, c => c.rank]);
}

/**
 * Adds all parent categories when they are not already included in the array,
 * but never adds level 1 (root) categories.
 * This function walks up the entire parent chain for each category, adding any missing parents,
 * stopping before reaching level 1.
 *
 * @param categories - The initial set of categories
 * @param allCategories - All available categories to find parents from
 * @returns Categories with all parent categories added (excluding level 1)
 */
export function addParentCategoriesWhenNotIncluded<
  T extends {
    id: string;
    parentId?: string | null;
    level: number;
    rank: number;
  },
>(categories: T[], allCategories: T[]): T[] {
  const categoriesMap = new Map(allCategories.map(c => [c.id, c]));
  const resultIds = new Set(categories.map(c => c.id));
  const result = [...categories];

  // Helper to add all parents, but never level 1
  const addParentsExceptLevel1 = (category: T): void => {
    if (!category.parentId) return;
    const parent = categoriesMap.get(category.parentId);
    if (!parent) return;

    // Never add level 1 categories
    if (parent.level <= 1) return;

    // Add parent if not already in result
    if (!resultIds.has(parent.id)) {
      result.push(parent);
      resultIds.add(parent.id);
    }

    // Continue recursively to add all ancestors
    addParentsExceptLevel1(parent);
  };

  // Process each category to add all missing parent categories
  categories.forEach(category => {
    addParentsExceptLevel1(category);
  });

  // Sort by level first, then by rank
  return sortBy(result, [c => c.level, c => c.rank]);
}

/**
 * Removes all subcategories if their 1st level (root) parent category id is "__deleted__".
 * This function traverses the category tree to find the root parent and filters out
 * all categories that descend from a deleted root category.
 *
 * @param categories - The categories to filter
 * @param allCategories - All available categories to traverse the parent chain
 * @returns Categories excluding those whose root parent is "__deleted__"
 */
export function removeSubcategoriesOfDeletedParent<
  T extends {
    id: string;
    parentId?: string | null;
    level: number;
  },
>(categories: T[], allCategories: T[]): T[] {
  const categoriesMap = new Map(allCategories.map(c => [c.id, c]));

  // Helper to find the root (level 1) parent of a category
  const findRootParent = (category: T): T | null => {
    // If this is level 1, it's the root
    if (category.level === 1) return category;

    // If no parent, this is a root (shouldn't happen for level > 1, but handle it)
    if (!category.parentId) return category;

    // Walk up to find the root parent
    let current = category;
    while (current.parentId) {
      const parent = categoriesMap.get(current.parentId);
      if (!parent) break;
      if (parent.level === 1) return parent;
      current = parent;
    }

    return current.level === 1 ? current : null;
  };

  // Filter out categories whose root parent is "__deleted__"
  return categories.filter(category => {
    const rootParent = findRootParent(category);
    return rootParent?.id !== "__deleted__";
  });
}
